import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
	View,
	Text,
	ScrollView,
	ImageBackground,
	Dimensions,
	TouchableOpacity,
	Animated,
	Platform,
	ActivityIndicator
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import useWindowDimensions from '../Hooks/useWindowDimensions';
import { useUser } from '../Widgets/UserContext';
import ContactCaptureService from '../services/ContactCaptureService';
import RSVPDataService from '../services/RSVPDataService';
import { getFirestore, doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { app } from '../firebase';
import HeaderBar, { useScrollHide } from '../Widgets/HeaderBar';
import styles from '../Styles/HubStyles';
import DashboardShareModal from '../Widgets/DashboardShareModal';
import { HEADER_PADDING } from "../constants";

// Import modular components
import ProfileOverview from './DashboardComponents/ProfileOverview';
import AchievementProgress from './DashboardComponents/AchievementProgress';
import EventManagement from './DashboardComponents/EventManagement';
import RegistryStatistics from './DashboardComponents/RegistryStatistics';
import PurchaseHistory from './DashboardComponents/PurchaseHistory';
import ContributionsHistory from './DashboardComponents/ContributionsHistory';
import YearbookMessages from './DashboardComponents/YearbookMessages';
import ThankYouCards from './DashboardComponents/ThankYouCards';
import DebugPanel from './DashboardComponents/DebugPanel';
import PageActivities from './DashboardComponents/PageActivities';
import ReminderTestingControls from './DashboardComponents/ReminderTestingControls';
import PollResults from './DashboardComponents/PollResults';
import GiftTracking from './DashboardComponents/GiftTracking';

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;

const GraduateDashboard = ({ navigation, route }) => {
	const { currentUser, currentUserData } = useUser();

	// Admin owner-preview support (same IIFE pattern as Registry/Events)
	const isAdminOwnerPreview = (() => {
		if (typeof window === 'undefined') return false;
		try {
			return new URLSearchParams(window.location.search).get('preview') === 'owner';
		} catch { return false; }
	})();

	const [previewUserData, setPreviewUserData] = useState(null);
	// effectiveUserData / effectiveEmail: use the target user in admin preview, otherwise the logged-in user
	const effectiveUserData = previewUserData || currentUserData;
	const effectiveEmail = previewUserData?.email || currentUserData?.email || currentUser?.email;
	const db = getFirestore(app);
	const { headerTranslateY, onScroll } = useScrollHide('scrollView');

	// When in admin owner-preview mode, load the target user's Firestore doc
	useEffect(() => {
		if (!isAdminOwnerPreview) return;
		const load = async () => {
			try {
				// Prefer target_email param for a direct, reliable doc lookup
				const targetEmail = (() => {
					try { return new URLSearchParams(window.location.search).get('target_email') || null; }
					catch { return null; }
				})();
				if (targetEmail) {
					const docSnap = await getDoc(doc(db, 'users', targetEmail.toLowerCase()));
					if (docSnap.exists()) {
						setPreviewUserData({ ...docSnap.data(), email: docSnap.data().email || docSnap.id });
						return;
					}
				}
				// Fallback: look up by uid field (for older URLs without target_email)
				const uid = (() => {
					try {
						if (route?.params?.uid) return route.params.uid;
						const match = window.location.pathname.match(/\/dashboard\/([^/?]+)/);
						return match?.[1] || null;
					} catch { return null; }
				})();
				if (!uid) return;
				const usersRef = collection(db, 'users');
				const q = query(usersRef, where('uid', '==', uid));
				const snap = await getDocs(q);
				if (!snap.empty) {
					const docSnap = snap.docs[0];
					setPreviewUserData({ ...docSnap.data(), email: docSnap.data().email || docSnap.id });
				}
			} catch { /* silent */ }
		};
		load();
	}, [isAdminOwnerPreview]); // eslint-disable-line react-hooks/exhaustive-deps

	// Feature flags - default to showing only essential components
	const [featureFlags, setFeatureFlags] = useState({
		dashboardGiftTracking: true,       // Primary feature - always on
		dashboardProfileOverview: false,   // Off - redundant with hub
		dashboardAchievements: false,      // Off - per client feedback
		dashboardEventManagement: false,   // Off - moving to RSVP widget
		dashboardRegistryStats: true,      // On - useful stats
		dashboardYearbookMessages: false,  // Off - less essential
		dashboardPollResults: false,       // Off - less essential
		dashboardPageActivities: true,     // On - useful engagement data
	});

	// State management
	const [isLoading, setIsLoading] = useState(true); // true = show spinner until first load completes
	const hasLoadedOnceRef = useRef(false); // tracks whether first data load has finished
	const [purchasedItems, setPurchasedItems] = useState([]);
	const [contributions, setContributions] = useState([]);
	const [yearBookMessages, setYearBookMessages] = useState([]);
	const [sentThankYouCards, setSentThankYouCards] = useState([]);
	const [thankYouCards, setThankYouCards] = useState({});
	const [sharedContacts, setSharedContacts] = useState([]);
	const [rsvpData, setRsvpData] = useState({ Going: [], Maybe: [], Decline: [] });
	const [rsvpUsers, setRsvpUsers] = useState([]);
	const [reminderSettings, setReminderSettings] = useState({ enabled: false, reminderTimes: [] });
	const [hasUnsavedReminderChanges, setHasUnsavedReminderChanges] = useState(false);
	const [polls, setPolls] = useState([]);
	const [dashboardStats, setDashboardStats] = useState({
		totalItems: 0,
		purchasedItems: 0,
		remainingItems: 0,
		totalValue: 0,
		purchasedValue: 0,
		contributionsValue: 0,
		totalRaised: 0
	});
	
	// Gift tracking state
	// Default to 0 (not 1000) - the actual goal will be loaded from goalTracker
	// This matches CombinedCard and Registry page behavior
	const [contributionGoal, setContributionGoal] = useState(0);
	const [amountContributed, setAmountContributed] = useState(0); // From goalTracker - authoritative source
	const [giftNotes, setGiftNotes] = useState({});
	const [registryItems, setRegistryItems] = useState([]); // All registry items for RegistryStatistics
	const [showGoalModal, setShowGoalModal] = useState(false); // For "Update Goal" CTA
	
	// Share modal state
	const [showShareModal, setShowShareModal] = useState(false);
	
	// Page-level animations
	const headerAnim = useRef(new Animated.Value(0)).current;
	const headerSlideAnim = useRef(new Animated.Value(-30)).current;
	const buttonScaleAnim = useRef(new Animated.Value(0.9)).current;
	const customizeButtonScale = useRef(new Animated.Value(1)).current;
	const doneButtonScale = useRef(new Animated.Value(1)).current;
	
	// ScrollView ref
	const scrollViewRef = useRef();
	
	// Widget refs for scrolling
	const widgetRefs = {
		giftTracking: useRef(),
		eventManagement: useRef(),
		registryStats: useRef(),
		pollResults: useRef(),
		yearbookMessages: useRef()
	};
	
	// Scroll to widget function
	const scrollToWidget = (widgetId, offset = 0) => {
		const ref = widgetRefs[widgetId]?.current;
		if (ref && scrollViewRef.current) {
			ref.measureLayout(scrollViewRef.current, (left, top, width, height) => {
				scrollViewRef.current.scrollTo({ y: top - 20 + offset, animated: true });
			}, () => {
				// Fallback if measureLayout fails
				ref.measure((x, y, width, height, pageX, pageY) => {
					scrollViewRef.current.scrollTo({ y: pageY - 20 + offset, animated: true });
				});
			});
		}
	};
	
	// Hover states
	const [customizeHovered, setCustomizeHovered] = useState(false);
	const [doneHovered, setDoneHovered] = useState(false);
	
	// Page entrance animations
	useEffect(() => {
		Animated.parallel([
			Animated.timing(headerAnim, {
				toValue: 1,
				duration: 600,
				useNativeDriver: true
			}),
			Animated.timing(headerSlideAnim, {
				toValue: 0,
				duration: 600,
				useNativeDriver: true
			}),
			Animated.spring(buttonScaleAnim, {
				toValue: 1,
				friction: 6,
				tension: 40,
				delay: 200,
				useNativeDriver: true
			})
		]).start();
	}, []);
	
	// Button hover animation helper
	const handleButtonHover = (animValue, isEntering) => {
		Animated.spring(animValue, {
			toValue: isEntering ? 1.05 : 1,
			friction: 8,
			useNativeDriver: true
		}).start();
	};
	
	// Button press animation helper
	const animateButtonPress = (animValue, callback) => {
		Animated.sequence([
			Animated.spring(animValue, {
				toValue: 0.92,
				friction: 8,
				useNativeDriver: true
			}),
			Animated.spring(animValue, {
				toValue: 1,
				friction: 8,
				useNativeDriver: true
			})
		]).start(() => callback && callback());
	};
	
	// Dashboard customization state - iOS-style editing
	const [isEditMode, setIsEditMode] = useState(false);
	
	// Grid layout configuration
	// Each widget has: id, size ('full', 'half', 'third'), row position
	const [widgetLayout, setWidgetLayout] = useState({
		// Desktop layout (2-3 columns)
		desktop: [
			{ id: 'giftTracking', size: 'full', visible: true },
			{ id: 'eventManagement', size: 'full', visible: true },
			{ id: 'registryStats', size: 'half', visible: true },
			{ id: 'pollResults', size: 'half', visible: true },
			{ id: 'yearbookMessages', size: 'full', visible: true },
		],
		// Mobile layout (single column)
		mobile: [
			{ id: 'giftTracking', size: 'full', visible: true },
			{ id: 'eventManagement', size: 'full', visible: true },
			{ id: 'registryStats', size: 'full', visible: true },
			{ id: 'pollResults', size: 'full', visible: true },
			{ id: 'yearbookMessages', size: 'full', visible: true },
		]
	});
	
	// Get current layout based on screen size
	const currentLayout = isMobile ? widgetLayout.mobile : widgetLayout.desktop;
	
	// Widget definitions with metadata
	const widgetDefinitions = {
		giftTracking: { 
			name: 'Gift Tracking', 
			description: 'Track gifts and thank you messages', 
			icon: 'gift',
			color: '#4c956c'
		},
		eventManagement: { 
			name: 'Event Management', 
			description: 'Manage RSVPs and reminders', 
			icon: 'calendar',
			color: '#3b82f6'
		},
		registryStats: { 
			name: 'Registry Stats', 
			description: 'View registry statistics', 
			icon: 'stats-chart',
			color: '#4c956c'
		},
		pollResults: { 
			name: 'Poll Results', 
			description: 'See how people voted', 
			icon: 'bar-chart',
			color: '#f97316'
		},
		yearbookMessages: { 
			name: 'Yearbook Messages', 
			description: 'Messages from friends & family', 
			icon: 'book',
			color: '#8b5cf6'
		}
	};

	// Fetch feature flags from Firestore with real-time updates
	useEffect(() => {
		const fetchFeatureFlags = () => {
			try {
				const isProduction = typeof window !== 'undefined' && window.location?.hostname === 'mytassel.com';
				const env = isProduction ? 'production' : 'staging';
				
				const configRef = doc(db, 'config', 'features');
				
				// Use onSnapshot for real-time updates
				const unsubscribe = onSnapshot(configRef, (configSnap) => {
					if (configSnap.exists()) {
						const flags = configSnap.data();
						
						const getFlagValue = (flag, defaultValue = false) => {
							if (flag === undefined) return defaultValue;
							if (typeof flag === 'boolean') return flag;
							return flag?.[env] ?? defaultValue;
						};
						
						setFeatureFlags({
							dashboardGiftTracking: getFlagValue(flags.dashboardGiftTracking, true),
							dashboardProfileOverview: getFlagValue(flags.dashboardProfileOverview, false),
							dashboardAchievements: getFlagValue(flags.dashboardAchievements, false),
							dashboardEventManagement: getFlagValue(flags.dashboardEventManagement, false),
							dashboardRegistryStats: getFlagValue(flags.dashboardRegistryStats, true),
							dashboardYearbookMessages: getFlagValue(flags.dashboardYearbookMessages, false),
							dashboardPollResults: getFlagValue(flags.dashboardPollResults, false),
							dashboardPageActivities: getFlagValue(flags.dashboardPageActivities, true),
						});
					}
				}, (error) => {
					// Keep defaults if fetch fails
				});
				
				// Return unsubscribe function for cleanup
				return unsubscribe;
			} catch (error) {
				// Keep defaults if fetch fails
			}
		};
		
		const unsubscribe = fetchFeatureFlags();
		
		// Cleanup on unmount
		return () => {
			if (unsubscribe) unsubscribe();
		};
	}, [db]);

	// Load widget layout from user's Firestore
	useEffect(() => {
		// In admin preview mode, skip until the target user's data is loaded
		if (isAdminOwnerPreview && !previewUserData) return;
		const loadWidgetLayout = async () => {
			const userEmail = effectiveEmail;
			if (!userEmail) return;
			
			try {
				const userDocRef = doc(db, 'users', userEmail);
				const userDocSnap = await getDoc(userDocRef);
				
				if (userDocSnap.exists()) {
					const data = userDocSnap.data();
					if (data.dashboardLayout) {
					
					// Listen for cross-component timeline updates and refresh dashboard data when they occur
					if (typeof window !== 'undefined') {
						const onTimelineUpdated = (e) => {
							try {
								const owner = e?.detail?.ownerEmail;
								// If owner matches current user, refresh the dashboard so EventManagement re-reads timeline events
								if (!owner || (currentUser && owner === (currentUser.email || '').toLowerCase())) {
									loadDashboardData();
								}
							} catch (err) {
								// ignore
							}
						};
						window.addEventListener('timelineUpdated', onTimelineUpdated);
						// Clean up on unmount
						const cleanup = () => { window.removeEventListener('timelineUpdated', onTimelineUpdated); };
						// Attach cleanup to unload
						if (typeof window !== 'undefined') window.addEventListener('beforeunload', cleanup);
					}
						// Migrate old widget IDs to new unified giftTracking
						const migrateLayout = (layout) => {
							if (!layout) return layout;
							
							// Check if layout has old widget IDs
							const oldIds = ['giftSummary', 'thankYouProgress', 'giftHistory'];
							const hasOldIds = layout.some(w => oldIds.includes(w.id));
							
							if (hasOldIds) {
								// Remove old IDs and add giftTracking at the start
								const filtered = layout.filter(w => !oldIds.includes(w.id));
								const hasGiftTracking = filtered.some(w => w.id === 'giftTracking');
								if (!hasGiftTracking) {
									return [{ id: 'giftTracking', size: 'full', visible: true }, ...filtered];
								}
								return filtered;
							}
							
							// Ensure giftTracking exists
							const hasGiftTracking = layout.some(w => w.id === 'giftTracking');
							if (!hasGiftTracking) {
								return [{ id: 'giftTracking', size: 'full', visible: true }, ...layout];
							}
							
							return layout;
						};
						
						const migratedLayout = {
							desktop: migrateLayout(data.dashboardLayout.desktop),
							mobile: migrateLayout(data.dashboardLayout.mobile)
						};
						
						setWidgetLayout(migratedLayout);
						
						// Save migrated layout back
						if (JSON.stringify(migratedLayout) !== JSON.stringify(data.dashboardLayout)) {
							saveWidgetLayout(migratedLayout);
						}
					}
				}
			} catch (error) {
				// Keep defaults if load fails
			}
		};
		
		loadWidgetLayout();
	}, [currentUserData?.email, currentUser?.email, effectiveEmail, isAdminOwnerPreview, previewUserData, db]);

	// Save widget layout to Firestore
	const saveWidgetLayout = async (newLayout) => {
		if (isAdminOwnerPreview) return;
		const userEmail = effectiveEmail;
		if (!userEmail) return;
		
		try {
			const userDocRef = doc(db, 'users', userEmail);
			await setDoc(userDocRef, { dashboardLayout: newLayout }, { merge: true });
		} catch (error) {
			// Silent fail
		}
	};

	// Toggle widget visibility
	const toggleWidgetVisibility = (widgetId) => {
		const layoutKey = isMobile ? 'mobile' : 'desktop';
		const newLayout = {
			...widgetLayout,
			[layoutKey]: widgetLayout[layoutKey].map(w => 
				w.id === widgetId ? { ...w, visible: !w.visible } : w
			)
		};
		setWidgetLayout(newLayout);
		saveWidgetLayout(newLayout);
	};

	// Change widget size
	const changeWidgetSize = (widgetId, newSize) => {
		const layoutKey = isMobile ? 'mobile' : 'desktop';
		const newLayout = {
			...widgetLayout,
			[layoutKey]: widgetLayout[layoutKey].map(w => 
				w.id === widgetId ? { ...w, size: newSize } : w
			)
		};
		setWidgetLayout(newLayout);
		saveWidgetLayout(newLayout);
	};

	// Reset layout to default
	const resetLayout = () => {
		const defaultLayout = {
			desktop: [
				{ id: 'giftTracking', size: 'full', visible: true },
				{ id: 'eventManagement', size: 'full', visible: true },
				{ id: 'registryStats', size: 'half', visible: true },
				{ id: 'pollResults', size: 'half', visible: true },
				{ id: 'yearbookMessages', size: 'full', visible: true },
			],
			mobile: [
				{ id: 'giftTracking', size: 'full', visible: true },
				{ id: 'eventManagement', size: 'full', visible: true },
				{ id: 'registryStats', size: 'full', visible: true },
				{ id: 'pollResults', size: 'full', visible: true },
				{ id: 'yearbookMessages', size: 'full', visible: true },
			]
		};
		setWidgetLayout(defaultLayout);
		saveWidgetLayout(defaultLayout);
	};

	// Get width style for widget size
	const getWidgetWidth = (size) => {
		if (isMobile) return '100%';
		switch (size) {
			case 'full': return '100%';
			case 'half': return 'calc(50% - 12px)';
			case 'third': return 'calc(33.333% - 16px)';
			default: return '100%';
		}
	};

	// Cycle through widget sizes (for corner handle clicks)
	const cycleWidgetSize = (widgetId) => {
		const layoutKey = isMobile ? 'mobile' : 'desktop';
		const currentWidget = widgetLayout[layoutKey].find(w => w.id === widgetId);
		if (!currentWidget) return;
		
		// Cycle: third → half → full → third
		const sizeOrder = ['third', 'half', 'full'];
		const currentIndex = sizeOrder.indexOf(currentWidget.size);
		const nextSize = sizeOrder[(currentIndex + 1) % sizeOrder.length];
		
		changeWidgetSize(widgetId, nextSize);
	};

	// Get size label for display
	const getSizeLabel = (size) => {
		switch (size) {
			case 'third': return 'S';
			case 'half': return 'M';
			case 'full': return 'L';
			default: return 'M';
		}
	};


	// Load gift tracking settings (contribution goal & notes)
	// Uses email-based path to be consistent with rest of app
	// Also loads goalTracker.targetAmount from registry as the PRIMARY goal source
	// This matches how CombinedCard and Registry page load goal data
	useEffect(() => {
		// In admin preview mode, skip until the target user's data is loaded
		if (isAdminOwnerPreview && !previewUserData) return;
		const loadGiftSettings = async () => {
			const userEmail = effectiveEmail;
			if (!userEmail) return;
			
			try {
				const userDocRef = doc(db, 'users', userEmail);
				const userDocSnap = await getDoc(userDocRef);
				
				if (userDocSnap.exists()) {
					const data = userDocSnap.data();
					const goalTracker = data.pages?.registryPageConfig?.goalTracker || {};
					
					// Get goal from registry's goalTracker (the authoritative source)
					// Parse as float to handle both string and number values
					const targetAmount = parseFloat(goalTracker.targetAmount || 0);
					if (targetAmount > 0) {
						setContributionGoal(targetAmount);
					}
					// Note: No fallback to giftTrackingSettings - goalTracker is the only source of truth
					// This matches the behavior of CombinedCard and Registry page
					
					// Set amount contributed from goalTracker
					const amountContrib = parseFloat(goalTracker.amountContributed || 0);
					setAmountContributed(amountContrib);
					
					// Load gift notes from giftTrackingSettings
					const giftSettings = data.giftTrackingSettings || {};
					if (giftSettings.giftNotes) setGiftNotes(giftSettings.giftNotes);
				}
			} catch (error) {
				// Silent fail for settings load
			}
		};
		
		loadGiftSettings();
	}, [currentUserData?.email, currentUser?.email, effectiveEmail, isAdminOwnerPreview, previewUserData, db]);

	// Handler to update contribution goal in Firestore
	// Updates goalTracker.targetAmount to match Registry page and CombinedCard
	const handleUpdateContributionGoal = async (newGoal) => {
		if (isAdminOwnerPreview) { setContributionGoal(newGoal); return; }
		setContributionGoal(newGoal);
		
		const userEmail = currentUserData?.email || currentUser?.email;
		if (!userEmail) return;
		
		try {
			const userDocRef = doc(db, 'users', userEmail);
			// Update goalTracker.targetAmount - the authoritative source
			// This ensures consistency with Registry page and CombinedCard
			await setDoc(userDocRef, { 
				pages: {
					registryPageConfig: {
						goalTracker: {
							targetAmount: newGoal
						}
					}
				}
			}, { merge: true });
		} catch (error) {
			// Silent fail for goal save
		}
	};

	// Handler to save gift note in Firestore
	const handleSaveGiftNote = async (giftId, note) => {
		if (isAdminOwnerPreview) { setGiftNotes(prev => ({ ...prev, [giftId]: note })); return; }
		const updatedNotes = { ...giftNotes, [giftId]: note };
		setGiftNotes(updatedNotes);
		
		const userEmail = currentUserData?.email || currentUser?.email;
		if (!userEmail) return;
		
		try {
			const userDocRef = doc(db, 'users', userEmail);
			await setDoc(userDocRef, { 
				giftTrackingSettings: { giftNotes: updatedNotes } 
			}, { merge: true });
		} catch (error) {
			// Silent fail for note save
		}
	};

	// Handler to add manual contribution (cash gift or item gift)
	const handleAddManualContribution = async (contributionData) => {
		if (isAdminOwnerPreview) return { success: false, error: 'Preview mode: changes not saved' };
		const userEmail = currentUserData?.email || currentUser?.email;
		if (!userEmail) return { success: false, error: 'No user email' };
		
		try {
			const userDocRef = doc(db, 'users', userEmail);
			
			if (contributionData.type === 'cash') {
				// Cash contribution - add to goalTracker
				const newAmount = parseFloat(contributionData.amount) || 0;
				const updatedAmount = amountContributed + newAmount;
				
				// Update amountContributed
				await updateDoc(userDocRef, {
					'pages.registryPageConfig.goalTracker.amountContributed': updatedAmount
				});
				
				// Add to contributions array
				const contributionEntry = {
					id: `manual-${Date.now()}`,
					amount: newAmount,
					contributorName: contributionData.contributorName || 'Anonymous',
					contributorEmail: contributionData.contributorEmail || '',
					contributorPhone: contributionData.contributorPhone || '',
					contributorId: null,
					isAnonymous: !contributionData.contributorName,
					message: contributionData.message || '',
					timestamp: new Date(),
					isManualEntry: true
				};
				
				await updateDoc(userDocRef, {
					'pages.registryPageConfig.goalTracker.contributions': arrayUnion(contributionEntry)
				});
				
				// Update local state
				setAmountContributed(updatedAmount);
				setContributions(prev => [...prev, {
					...contributionEntry,
					contributor: contributionEntry.contributorName,
					contributorPhotoURL: '',
					contributionDate: contributionEntry.timestamp,
				}]);
				
				return { success: true };
			} else {
				// Gift item contribution - add to purchased items
				const giftEntry = {
					id: `manual-gift-${Date.now()}`,
					name: contributionData.itemName || 'Gift',
					description: contributionData.itemDescription || '',
					price: `$${parseFloat(contributionData.amount || 0).toFixed(2)}`,
					purchased: true,
					purchaseTime: new Date().toLocaleString(),
					purchasedBy: contributionData.contributorName ? {
						name: contributionData.contributorName,
						email: contributionData.contributorEmail || '',
						phone: contributionData.contributorPhone || ''
					} : null,
					isAnonymousPurchase: !contributionData.contributorName,
					giftMessage: contributionData.message || '',
					isManualEntry: true
				};
				
				// Get current items and add new one
				const userDocSnap = await getDoc(userDocRef);
				if (userDocSnap.exists()) {
					const currentItems = userDocSnap.data().pages?.registryPageConfig?.items || [];
					await updateDoc(userDocRef, {
						'pages.registryPageConfig.items': [...currentItems, giftEntry]
					});
				}
				
				// Update local state
				setPurchasedItems(prev => [...prev, {
					...giftEntry,
					purchaser: giftEntry.purchasedBy || 'Anonymous',
					purchaseDate: giftEntry.purchaseTime,
				}]);
				
				return { success: true };
			}
		} catch (error) {
			return { success: false, error: error.message };
		}
	};

	// Handler to hide/unhide yearbook message
	const handleHideYearbookMessage = async (message) => {
		if (isAdminOwnerPreview) return;
		const userEmail = currentUserData?.email || currentUser?.email;
		if (!userEmail) return;
		
		try {
			const userDocRef = doc(db, 'users', userEmail);
			const userDocSnap = await getDoc(userDocRef);
			
			if (userDocSnap.exists()) {
				const data = userDocSnap.data();
				const currentMessages = data.messages?.yearbookMessages || [];
				const updatedMessages = currentMessages.map(msg => 
					msg.id === message.id 
						? { ...msg, hidden: !msg.hidden }
						: msg
				);
				
				await updateDoc(userDocRef, {
					'messages.yearbookMessages': updatedMessages
				});
				
				// Update local state
				setYearBookMessages(prev => prev.map(msg => 
					msg.id === message.id 
						? { ...msg, hidden: !msg.hidden }
						: msg
				));
			}
		} catch (error) {
			// Silent fail
		}
	};

	// Handler to delete yearbook message
	const handleDeleteYearbookMessage = async (message) => {
		if (isAdminOwnerPreview) return;
		const userEmail = currentUserData?.email || currentUser?.email;
		if (!userEmail) return;
		
		try {
			const userDocRef = doc(db, 'users', userEmail);
			const userDocSnap = await getDoc(userDocRef);
			
			if (userDocSnap.exists()) {
				const data = userDocSnap.data();
				const currentMessages = data.messages?.yearbookMessages || [];
				const updatedMessages = currentMessages.filter(msg => msg.id !== message.id);
				
				await updateDoc(userDocRef, {
					'messages.yearbookMessages': updatedMessages
				});
				
				// Update local state
				setYearBookMessages(prev => prev.filter(msg => msg.id !== message.id));
			}
		} catch (error) {
			// Silent fail
		}
	};

	// Handler to reply to yearbook message (creates a new message as a reply)
	// Align fields with Registry Yearbook implementation so replies render correctly.
	const handleReplyYearbookMessage = async (originalMessage, replyText) => {
		if (isAdminOwnerPreview) return;
		const userEmail = currentUserData?.email || currentUser?.email;
		if (!userEmail || !replyText.trim()) return;

		try {
			const userDocRef = doc(db, 'users', userEmail);

			const replyMessage = {
				id: `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
				text: replyText.trim(),
				image: null,
				senderUID: currentUserData?.uid || currentUser?.uid || 'anonymous',
				senderFirstName: currentUserData?.firstName || 'Anonymous',
				senderLastName: currentUserData?.lastName || '',
				senderPhotoURL: currentUserData?.profilePictureURL || currentUser?.photoURL || null,
				sessionId: null,
				timestamp: Date.now(),
				createdBy: currentUserData?.uid || currentUser?.uid || 'anonymous',
				isReply: true,
				replyToId: originalMessage.id || null,
				originalSenderName: `${originalMessage.senderFirstName || ''} ${originalMessage.senderLastName || ''}`.trim(),
				originalMessageText: originalMessage.text || originalMessage.message || ''
			};

			await updateDoc(userDocRef, {
				'messages.yearbookMessages': arrayUnion(replyMessage)
			});

			// Update local state
			setYearBookMessages(prev => [...prev, replyMessage]);
		} catch (error) {
			// Silent fail
		}
	};

	// Handler for "Update Goal" from RegistryStatistics
	const handleOpenGoalModal = () => {
		setShowGoalModal(true);
	};

	// Services
	const contactCaptureService = new ContactCaptureService();

	// Load dashboard data
	const loadDashboardData = useCallback(async () => {
		try {
			setIsLoading(true);
			
			// Load shared contacts / reminder recipients using server API (enriched with unsubscribe + RSVP tags when available)
			const userEmailId = effectiveEmail;
			const effectiveUid = effectiveUserData?.uid;
			if (userEmailId) {
				try {
					const API_BASE = 'https://us-central1-my-tassel.cloudfunctions.net/api';
					const resp = await fetch(`${API_BASE}/get-reminder-recipients`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ eventOwnerId: userEmailId, includeSharedContacts: true })
					});
					if (resp.ok) {
						const json = await resp.json();
						if (json && Array.isArray(json.recipients)) {
							const isInviteSource = s => s === 'shared' || s === 'invite' || s === 'sms_share' || s === 'email_share';
							const sharedOnly = json.recipients.filter(r => isInviteSource(r.source));
							setSharedContacts(sharedOnly);
						} else {
							// Fallback to ContactCaptureService if API doesn't return recipients
							if (effectiveUid) {
								const contacts = await contactCaptureService.getSharedContacts(effectiveUid);
								setSharedContacts(contacts);
							}
						}
					} else {
						// API failed - fallback to ContactCaptureService
						if (effectiveUid) {
							const contacts = await contactCaptureService.getSharedContacts(effectiveUid);
							setSharedContacts(contacts);
						}
					}
				} catch (err) {
					// On error, fallback to ContactCaptureService
					if (effectiveUid) {
						const contacts = await contactCaptureService.getSharedContacts(effectiveUid);
						setSharedContacts(contacts);
					}
				}
			}
			
		// Fetch fresh user data from the graduation page owner - use EMAIL as document ID like RSVP widget does
		let freshUserData = previewUserData || currentUserData;
		const userEmail = effectiveEmail; // Use email as document ID, not UID
		if (userEmail) {
			try {
				const userDocRef = doc(db, "users", userEmail);
				const userDoc = await getDoc(userDocRef);
				if (userDoc.exists()) {
					freshUserData = userDoc.data();
				}
			} catch (error) {
				// Fall back to currentUserData if fetch fails
			}
		}
		
		// Use fresh user data instead of cached currentUserData
		const registryConfig = freshUserData.pages?.registryPageConfig;
		const eventsConfig = freshUserData.pages?.eventsPageConfig;
		
		// Load sent thank you cards from user data
		if (freshUserData.thankYouCards) {
			setSentThankYouCards(freshUserData.thankYouCards);
		}
		
		// Load reminder settings from events config - but don't overwrite unsaved changes
		if (eventsConfig?.reminderSettings && !hasUnsavedReminderChanges) {
			setReminderSettings({
				enabled: eventsConfig.reminderSettings.enabled || false,
				reminderTimes: eventsConfig.reminderSettings.reminderTimes || []
			});
		} else if (!eventsConfig?.reminderSettings && !hasUnsavedReminderChanges) {
			// Set default reminder settings if none exist and no unsaved changes
			setReminderSettings({ enabled: false, reminderTimes: [] });
		}
		
		// Load RSVP data using modular service
		
		let rsvpCounts = { Going: [], Maybe: [], Decline: [] };
		if (eventsConfig || freshUserData) {
			rsvpCounts = RSVPDataService.extractRsvpDataFromUserData(freshUserData, eventsConfig);
			setRsvpData(rsvpCounts);
		}
				
		// Get user details for RSVP'd users using modular service
		const rsvpUsersWithStatus = RSVPDataService.getProcessedRsvpUsers(rsvpCounts);
		
		setRsvpUsers(rsvpUsersWithStatus);
		
		// Helper to fetch user profile picture from Firestore by email
		const fetchUserProfilePicture = async (email) => {
			if (!email) return null;
			try {
				const userDocRef = doc(db, 'users', email);
				const userDocSnap = await getDoc(userDocRef);
				if (userDocSnap.exists()) {
					const userData = userDocSnap.data();
					return userData.profilePictureURL || userData.photoURL || userData.profilePhoto || null;
				}
			} catch (error) {
				// Silent fail
			}
			return null;
		};
		
		// Helper to fetch user profile picture from Firestore by UID (for yearbook messages)
		const fetchUserProfilePictureByUID = async (uid) => {
			if (!uid || uid === 'anonymous') return null;
			try {
				const usersRef = collection(db, 'users');
				const q = query(usersRef, where('uid', '==', uid));
				const querySnapshot = await getDocs(q);
				if (!querySnapshot.empty) {
					const userData = querySnapshot.docs[0].data();
					return userData.profilePictureURL || userData.photoURL || userData.profilePhoto || null;
				}
			} catch (error) {
				// Silent fail
			}
			return null;
		};
			
		// Process registry data
		if (registryConfig) {
				const allItems = registryConfig.items || [];
				const visibleItems = allItems.filter(item => !item.deletedFromRegistry); // Items still on registry
				const purchased = allItems.filter(item => item.purchased); // All purchased items, including deleted from registry
				
				// Store visible items for RegistryStatistics expandable list
				setRegistryItems(visibleItems);
				
				// Get goalTracker data for contributions
				const goalTracker = registryConfig.goalTracker || {};
				
				// Calculate contributions total from goalTracker.amountContributed (the accurate source)
				const contributionsTotal = parseFloat(goalTracker.amountContributed || 0);

				// Calculate stats - use visibleItems for registry counts, purchased for gift totals
				const purchasedValue = purchased.reduce((sum, item) => sum + parseFloat(item.price?.replace('$', '') || 0), 0);
				const visiblePurchased = visibleItems.filter(item => item.purchased);
				const stats = {
					totalItems: visibleItems.length,
					purchasedItems: visiblePurchased.length,
					remainingItems: visibleItems.length - visiblePurchased.length,
					totalValue: visibleItems.reduce((sum, item) => sum + parseFloat(item.price?.replace('$', '') || 0), 0),
					purchasedValue: purchasedValue,
					contributionsValue: contributionsTotal,
					totalRaised: purchasedValue + contributionsTotal
				};
				
				setDashboardStats(stats);
				
				// Get purchased items with full details
				const purchasedItemsWithDetails = purchased.map(item => {
					// Handle purchasedBy - could be string or object
					const purchaserData = item.purchasedBy;
					let purchaser;
					
					if (item.isAnonymousPurchase) {
						purchaser = 'Anonymous';
					} else if (typeof purchaserData === 'object' && purchaserData !== null) {
						// Keep the full object with name, email, phone, photoURL
						purchaser = {
							name: purchaserData.name || purchaserData.displayName || 'Anonymous',
							email: purchaserData.email || '',
							phone: purchaserData.phone || purchaserData.phoneNumber || '',
							photoURL: purchaserData.photoURL || purchaserData.profilePictureURL || purchaserData.profilePhoto || purchaserData.avatar || ''
						};
					} else if (typeof purchaserData === 'string') {
						purchaser = { name: purchaserData };
					} else {
						purchaser = 'Anonymous';
					}
					
					return {
						...item,
						purchaser,
						purchaseDate: item.purchaseTime || 'Unknown',
						giftMessage: item.giftMessage || ''
					};
				});
				
				// Load contributions (direct monetary gifts). Older contribution flow records were
				// stored at registryPageConfig.contributions while the dashboard expects the
				// canonical goalTracker.contributions array, so merge both for existing users.
				let contributionsWithDetails = [];
				const goalTrackerContributions = Array.isArray(goalTracker.contributions) ? goalTracker.contributions : [];
				const legacyContributions = Array.isArray(registryConfig.contributions) ? registryConfig.contributions : [];
				const contributionKey = (contribution) => [
					contribution.id || contribution.paymentIntentId || '',
					contribution.contributorEmail || contribution.email || '',
					contribution.contributorName || contribution.name || '',
					contribution.amount || '',
					contribution.timestamp?.seconds || contribution.timestamp || contribution.createdAt || ''
				].join('|');
				const seenContributionKeys = new Set();
				const allContributions = [...goalTrackerContributions, ...legacyContributions].filter(contribution => {
					const key = contributionKey(contribution);
					if (seenContributionKeys.has(key)) return false;
					seenContributionKeys.add(key);
					return true;
				});
				if (allContributions.length > 0) {
					contributionsWithDetails = allContributions.map(contribution => ({
						...contribution,
						contributor: contribution.isAnonymous ? 'Anonymous' : (contribution.contributorName || contribution.name || 'Anonymous'),
						contributorEmail: contribution.contributorEmail || contribution.email || '',
						contributorPhone: contribution.contributorPhone || contribution.phone || '',
						contributorPhotoURL: contribution.contributorPhotoURL || contribution.photoURL || contribution.profilePictureURL || contribution.avatar || '',
						contributorId: contribution.contributorId || '',
						contributionDate: contribution.timestamp || contribution.createdAt || contribution.date || 'Unknown',
						message: contribution.message || '',
						amount: contribution.amount || 0
					}));
				}
				
				// Fetch profile pictures from Firestore for purchasers/contributors who don't have one
				const emailsToFetch = new Set();
				purchasedItemsWithDetails.forEach(item => {
					if (item.purchaser?.email && !item.purchaser?.photoURL) {
						emailsToFetch.add(item.purchaser.email);
					}
				});
				contributionsWithDetails.forEach(contrib => {
					if (contrib.contributorEmail && !contrib.contributorPhotoURL) {
						emailsToFetch.add(contrib.contributorEmail);
					}
				});
				
				// Fetch profile pictures in parallel
				const profilePictureCache = {};
				if (emailsToFetch.size > 0) {
					const fetchPromises = Array.from(emailsToFetch).map(async (email) => {
						const photoURL = await fetchUserProfilePicture(email);
						if (photoURL) {
							profilePictureCache[email] = photoURL;
						}
					});
					await Promise.all(fetchPromises);
				}
				
				// Update items with fetched profile pictures
				const updatedPurchasedItems = purchasedItemsWithDetails.map(item => {
					if (item.purchaser?.email && profilePictureCache[item.purchaser.email]) {
						return {
							...item,
							purchaser: {
								...item.purchaser,
								photoURL: profilePictureCache[item.purchaser.email]
							}
						};
					}
					return item;
				});
				
				const updatedContributions = contributionsWithDetails.map(contrib => {
					if (contrib.contributorEmail && profilePictureCache[contrib.contributorEmail]) {
						return {
							...contrib,
							contributorPhotoURL: profilePictureCache[contrib.contributorEmail]
						};
					}
					return contrib;
				});
				
				setPurchasedItems(updatedPurchasedItems);
				setContributions(updatedContributions);
				
				// Also fetch profile pictures for yearbook messages using senderUID
				const messages = freshUserData?.messages?.yearbookMessages || [];
				if (messages.length > 0) {
					const messageUIDsToFetch = new Set();
					messages.forEach(msg => {
						// Use senderUID like CombinedCard does
						if (msg.senderUID && msg.senderUID !== 'anonymous' && !msg.senderPhotoURL) {
							messageUIDsToFetch.add(msg.senderUID);
						}
					});
					
					// Fetch profile pictures for message senders by UID
					const messagePictureCache = {};
					if (messageUIDsToFetch.size > 0) {
						const msgFetchPromises = Array.from(messageUIDsToFetch).map(async (uid) => {
							const photoURL = await fetchUserProfilePictureByUID(uid);
							if (photoURL) {
								messagePictureCache[uid] = photoURL;
							}
						});
						await Promise.all(msgFetchPromises);
					}
					
					// Update messages with profile pictures
					const updatedMessages = messages.map(msg => {
						if (msg.senderUID && messagePictureCache[msg.senderUID]) {
							return {
								...msg,
								senderPhotoURL: messagePictureCache[msg.senderUID]
							};
						}
						return msg;
					});
					setYearBookMessages(updatedMessages);
				} else {
					setYearBookMessages(messages);
				}
			} else {
				// No registry config, still load yearbook messages with profile pictures
				const messages = freshUserData?.messages?.yearbookMessages || [];
				if (messages.length > 0) {
					const messageUIDsToFetch = new Set();
					messages.forEach(msg => {
						if (msg.senderUID && msg.senderUID !== 'anonymous' && !msg.senderPhotoURL) {
							messageUIDsToFetch.add(msg.senderUID);
						}
					});
					
					const messagePictureCache = {};
					if (messageUIDsToFetch.size > 0) {
						const msgFetchPromises = Array.from(messageUIDsToFetch).map(async (uid) => {
							const photoURL = await fetchUserProfilePictureByUID(uid);
							if (photoURL) {
								messagePictureCache[uid] = photoURL;
							}
						});
						await Promise.all(msgFetchPromises);
					}
					
					const updatedMessages = messages.map(msg => {
						if (msg.senderUID && messagePictureCache[msg.senderUID]) {
							return { ...msg, senderPhotoURL: messagePictureCache[msg.senderUID] };
						}
						return msg;
					});
					setYearBookMessages(updatedMessages);
				} else {
					setYearBookMessages(messages);
				}
			}
			
			// Load poll data from events config
			if (eventsConfig && eventsConfig.cards) {
				const pollCards = eventsConfig.cards.filter(card => card.type === 'Poll');
				setPolls(pollCards);
			} else {
				setPolls([]);
			}
			
		} catch (error) {
			// Silent fail for dashboard data load
		} finally {
			hasLoadedOnceRef.current = true;
			setIsLoading(false);
		}
	}, [currentUser?.uid, currentUserData, hasUnsavedReminderChanges, effectiveEmail, previewUserData]);

	// Load data on component mount and when user data changes
	useEffect(() => {
		if (isAdminOwnerPreview) {
			// In preview mode, only load once the target user's data is ready
			if (previewUserData) loadDashboardData();
		} else if (currentUser && currentUserData) {
			loadDashboardData();
		}
	}, [currentUser, currentUserData, loadDashboardData, previewUserData]);

	// Auto-refresh every 30 seconds (skip in admin preview to avoid thrashing)
	useEffect(() => {
		if (isAdminOwnerPreview) return;
		const interval = setInterval(() => {
			if (currentUser && currentUserData) {
				loadDashboardData();
			}
		}, 30000);

		return () => clearInterval(interval);
	}, [currentUser, currentUserData, loadDashboardData]);

	// Helper functions
	const updateReminderSettings = (newSettings) => {
		if (isAdminOwnerPreview) return;
		setReminderSettings(newSettings);
		setHasUnsavedReminderChanges(true);
	};

	const markReminderSettingsSaved = () => {
		setHasUnsavedReminderChanges(false);
	};

	// Handle sending thank you cards
	const handleSendThankYouCard = async (cardData) => {
		if (isAdminOwnerPreview) throw new Error('Preview mode: thank you cards cannot be sent.');
		try {
			const { message, emailRecipients = [], smsRecipients = [], sendMethod, senderName } = cardData;
			
			// Prepare the request payload
			const payload = {
				senderName: senderName || currentUserData?.displayName || 'Graduate',
				senderEmail: currentUserData?.email || '',
				message: message,
				emailRecipients: emailRecipients,
				smsRecipients: smsRecipients,
				personalizedMessages: cardData.personalizedMessages || []
			};
			
			// Call the Firebase function
			const response = await fetch('https://us-central1-my-tassel.cloudfunctions.net/api/thank-you-card', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload)
			});

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || 'Failed to send thank you cards');
			}

			// Store sent cards in Firestore
			const db = getFirestore(app);
			const userEmail = currentUserData?.email;
			
			if (userEmail) {
				const userRef = doc(db, 'users', userEmail);
				
				// Add new cards to the user's record (both email and SMS)
				const newCards = [];
				
				// Add email cards
				if (result.results?.email) {
					result.results.email.forEach(emailResult => {
						newCards.push({
							recipientEmail: emailResult.recipient,
							recipientName: emailResult.recipient.split('@')[0],
							recipientType: 'email',
							message: message,
							cardName: 'Thank You Card',
							sentDate: emailResult.sentAt || new Date().toISOString(),
							status: emailResult.status
						});
					});
				}
				
				// Add SMS cards
				if (result.results?.sms) {
					result.results.sms.forEach(smsResult => {
						newCards.push({
							recipientPhone: smsResult.recipient,
							recipientName: smsResult.recipient,
							recipientType: 'sms',
							message: message,
							cardName: 'Thank You Card',
							sentDate: smsResult.sentAt || new Date().toISOString(),
							status: smsResult.status
						});
					});
				}

				// Update local state immediately using callback to build on current state
				setSentThankYouCards(prev => [...prev, ...newCards]);

				// Persist to Firestore - read fresh from DB to avoid stale data
				const { updateDoc, getDoc: getDocFresh } = await import('firebase/firestore');
				const freshDoc = await getDocFresh(userRef);
				const existingCards = freshDoc.exists() ? (freshDoc.data().thankYouCards || []) : [];
				await updateDoc(userRef, {
					thankYouCards: [...existingCards, ...newCards]
				});
			}

			return result;

		} catch (error) {
			throw error;
		}
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(amount);
	};

	const formatDate = (dateValue) => {
		if (!dateValue || dateValue === 'Unknown') return 'Unknown';
		try {
			// Handle Firebase timestamp objects
			let date;
			if (dateValue && typeof dateValue === 'object' && dateValue.seconds) {
				// Firebase timestamp object
				date = new Date(dateValue.seconds * 1000);
			} else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
				// Regular date string or timestamp
				date = new Date(dateValue);
			} else {
				return String(dateValue);
			}
			
			if (isNaN(date.getTime())) {
				return String(dateValue);
			}
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit'
			});
		} catch {
			return String(dateValue || 'Unknown');
		}
	};

	// Styles - MyTassel themed
	const containerStyle = {
		flex: 1,
		paddingHorizontal: isMobile ? 16 : 48,
		paddingBottom: 40,
		maxWidth: 1200,
		alignSelf: 'center',
		width: '100%'
	};

	const cardStyle = {
		backgroundColor: 'rgba(254, 253, 226, 0.95)',
		borderRadius: 20,
		padding: isMobile ? 20 : 24,
		marginBottom: 24,
		shadowColor: '#1a5c3a',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.15,
		shadowRadius: 12,
		elevation: 4,
		borderWidth: 1,
		borderColor: 'rgba(76, 149, 108, 0.1)'
	};

	const statGridStyle = {
		flexDirection: isMobile ? 'column' : 'row',
		justifyContent: 'space-between',
		gap: isMobile ? 12 : 16,
		flexWrap: 'wrap'
	};

	const statBoxStyle = {
		flex: isMobile ? 0 : 1,
		minWidth: isMobile ? '100%' : 140,
		backgroundColor: 'rgba(245, 240, 225, 0.8)',
		padding: isMobile ? 16 : 20,
		borderRadius: 16,
		alignItems: 'center'
	};

	const tableHeaderStyle = {
		flexDirection: 'row',
		backgroundColor: 'rgba(245, 240, 225, 0.8)',
		padding: isMobile ? 12 : 16,
		borderRadius: 12,
		marginBottom: 8
	};

	const tableRowStyle = {
		flexDirection: 'row',
		padding: isMobile ? 14 : 16,
		borderBottomWidth: 1,
		borderBottomColor: 'rgba(76, 149, 108, 0.1)',
		alignItems: 'center'
	};

	return (
		<ImageBackground 
			source={require("../assets/Hub Background.png")} 
			style={[styles.backgroundImage, { flex: 1 }]}
		>
			<HeaderBar 
				title="My Dashboard"
				navigation={navigation}
				showBackButton={true}
				headerTranslateY={headerTranslateY}
			/>
			<ScrollView ref={scrollViewRef} style={styles.body} onScroll={onScroll} contentContainerStyle={{ flexGrow: 1, paddingTop: isMobile ? HEADER_PADDING.mobile : HEADER_PADDING.desktop }}>
				<View style={containerStyle}>
					{/* Admin owner-preview notice */}
					{isAdminOwnerPreview && (
						<View style={{ backgroundColor: 'rgba(44,90,63,0.92)', borderRadius: 12, paddingVertical: 10, paddingHorizontal: 18, marginBottom: 20, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
							<Text style={{ fontFamily: 'Orpheus Pro', color: '#fefde2', fontSize: 13 }}>
								Admin preview — viewing {effectiveUserData?.firstName ? `${effectiveUserData.firstName}'s` : 'this'} dashboard as owner. Changes are not saved.
							</Text>
						</View>
					)}
					{/* Page Header with Animations */}
					<Animated.View style={{
						marginBottom: 32,
						flexDirection: isMobile ? 'column' : 'row',
						justifyContent: 'space-between',
						alignItems: isMobile ? 'flex-start' : 'center',
						gap: isMobile ? 16 : 0,
						opacity: headerAnim,
						transform: [{ translateY: headerSlideAnim }]
					}}>
						<View>
							<Text style={{
								fontSize: isMobile ? 36 : 48,
								fontFamily: 'Bodoni72Smallcaps',
								color: '#fefde2',
								marginBottom: 8,
								textShadowColor: 'rgba(0, 0, 0, 0.3)',
								textShadowOffset: { width: 2, height: 2 },
								textShadowRadius: 4
							}}>
								Welcome{effectiveUserData?.firstName ? `, ${effectiveUserData.firstName}` : ''}
							</Text>
							<Text style={{
								fontSize: isMobile ? 15 : 18,
								fontFamily: 'Orpheus Pro',
								color: '#fefde2',
								opacity: 0.9,
								lineHeight: isMobile ? 22 : 26
							}}>
								Track your gifts and send thank you messages
							</Text>
						</View>
						
						{/* Edit Mode / Customize Buttons with Animations */}
						<Animated.View style={{ 
							flexDirection: 'row', 
							gap: 12,
							transform: [{ scale: buttonScaleAnim }]
						}}>
							{isEditMode ? (
								<Animated.View style={{ transform: [{ scale: doneButtonScale }] }}>
									<TouchableOpacity
										onPress={() => animateButtonPress(doneButtonScale, () => setIsEditMode(false))}
										onMouseEnter={() => {
											setDoneHovered(true);
											handleButtonHover(doneButtonScale, true);
										}}
										onMouseLeave={() => {
											setDoneHovered(false);
											handleButtonHover(doneButtonScale, false);
										}}
										activeOpacity={0.9}
										style={{
											flexDirection: 'row',
											alignItems: 'center',
											backgroundColor: doneHovered ? '#3d7a58' : '#4c956c',
											paddingVertical: 12,
											paddingHorizontal: 20,
											borderRadius: 25,
											gap: 8,
											shadowColor: '#4c956c',
											shadowOffset: { width: 0, height: doneHovered ? 8 : 4 },
											shadowOpacity: doneHovered ? 0.5 : 0.3,
											shadowRadius: doneHovered ? 12 : 8,
											elevation: doneHovered ? 6 : 4,
											...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
										}}
									>
										<Ionicons name="checkmark" size={20} color="#fff" />
										<Text style={{
											color: '#fff',
											fontSize: 15,
											fontFamily: 'Orpheus Pro',
											fontWeight: '600'
										}}>
											Done
										</Text>
									</TouchableOpacity>
								</Animated.View>
							) : (
								<Animated.View style={{ transform: [{ scale: customizeButtonScale }] }}>
									<TouchableOpacity
										onPress={() => animateButtonPress(customizeButtonScale, () => setIsEditMode(true))}
										onMouseEnter={() => {
											setCustomizeHovered(true);
											handleButtonHover(customizeButtonScale, true);
										}}
										onMouseLeave={() => {
											setCustomizeHovered(false);
											handleButtonHover(customizeButtonScale, false);
										}}
										activeOpacity={0.9}
										style={{
											flexDirection: 'row',
											alignItems: 'center',
											backgroundColor: customizeHovered ? 'rgba(254, 253, 226, 0.25)' : 'rgba(254, 253, 226, 0.15)',
											paddingVertical: 12,
											paddingHorizontal: 20,
											borderRadius: 25,
											borderWidth: 1,
											borderColor: customizeHovered ? 'rgba(254, 253, 226, 0.5)' : 'rgba(254, 253, 226, 0.3)',
											gap: 8,
											shadowColor: '#fefde2',
											shadowOffset: { width: 0, height: customizeHovered ? 6 : 0 },
											shadowOpacity: customizeHovered ? 0.3 : 0,
											shadowRadius: customizeHovered ? 10 : 0,
											...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.2s ease' } : {})
										}}
									>
										<Ionicons name="grid-outline" size={20} color="#fefde2" />
										<Text style={{
											color: '#fefde2',
											fontSize: 15,
											fontFamily: 'Orpheus Pro',
											fontWeight: '500'
										}}>
											Edit Layout
										</Text>
									</TouchableOpacity>
								</Animated.View>
							)}
						</Animated.View>
					</Animated.View>

					{/* Edit Mode Banner */}
					{isEditMode && (
						<View style={{
							backgroundColor: 'rgba(76, 149, 108, 0.95)',
							padding: isMobile ? 14 : 18,
							borderRadius: 16,
							marginBottom: 24,
							flexDirection: isMobile ? 'column' : 'row',
							alignItems: isMobile ? 'stretch' : 'center',
							justifyContent: 'space-between',
							gap: isMobile ? 14 : 0,
							shadowColor: '#4c956c',
							shadowOffset: { width: 0, height: 4 },
							shadowOpacity: 0.3,
							shadowRadius: 12,
							elevation: 6
						}}>
							<View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, flex: 1 }}>
								<View style={{
									width: 44,
									height: 44,
									borderRadius: 12,
									backgroundColor: 'rgba(255,255,255,0.2)',
									justifyContent: 'center',
									alignItems: 'center'
								}}>
									<Ionicons name="apps" size={24} color="#fff" />
								</View>
								<View style={{ flex: 1 }}>
									<Text style={{ color: '#fff', fontSize: 17, fontWeight: '700' }}>
										Editing Layout
									</Text>
									<Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 2 }}>
										Tap corner buttons to resize (S/M/L)
									</Text>
								</View>
							</View>
							<View style={{ flexDirection: 'row', gap: 10 }}>
								<TouchableOpacity
									onPress={resetLayout}
									activeOpacity={0.8}
									style={{
										backgroundColor: 'rgba(255,255,255,0.2)',
										paddingVertical: 10,
										paddingHorizontal: 16,
										borderRadius: 10,
										flexDirection: 'row',
										alignItems: 'center',
										gap: 6
									}}
								>
									<Ionicons name="refresh" size={16} color="#fff" />
									<Text style={{ color: '#fff', fontSize: 14, fontWeight: '500' }}>Reset</Text>
								</TouchableOpacity>
							</View>
						</View>
					)}

					{/* Initial loading state — shown until first data load completes */}
					{isLoading && !hasLoadedOnceRef.current && (
						<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 80 }}>
							<ActivityIndicator size="large" color="#fefde2" />
						</View>
					)}

					{/* Smart Grid Layout */}
					{(!isLoading || hasLoadedOnceRef.current) && <View style={{
						flexDirection: 'row',
						flexWrap: 'wrap',
						gap: 24,
						marginBottom: 24,
						alignItems: 'stretch'
					}}>
						{currentLayout.map((widgetItem) => {
							const widget = widgetDefinitions[widgetItem.id];
							if (!widget) return null;
							if (!widgetItem.visible && !isEditMode) return null;
							
							// Check if the widget is enabled by admin feature flags
							const featureFlagKey = `dashboard${widgetItem.id.charAt(0).toUpperCase() + widgetItem.id.slice(1)}`;
							const isFeatureEnabled = featureFlags[featureFlagKey];
							if (!isFeatureEnabled) return null;
							
							// Render the actual widget content
							const renderWidgetContent = () => {
								switch (widgetItem.id) {
									case 'giftTracking':
										return (
											<GiftTracking
												purchasedItems={purchasedItems}
												contributions={contributions}
												sentThankYouCards={sentThankYouCards}
												onSendBulkThankYou={handleSendThankYouCard}
												formatDate={formatDate}
												formatCurrency={formatCurrency}
												isMobile={isMobile}
												senderName={effectiveUserData?.firstName || effectiveUserData?.displayName || 'Graduate'}
												contributionGoal={contributionGoal}
												amountContributed={amountContributed}
												onUpdateContributionGoal={handleUpdateContributionGoal}
												onAddManualContribution={handleAddManualContribution}
												giftNotes={giftNotes}
												onSaveGiftNote={handleSaveGiftNote}
												eventId={effectiveUserData?.eventId || effectiveUserData?.uid}
												onSharePage={() => setShowShareModal(true)}
												onScrollToWidget={scrollToWidget}
												totalItems={dashboardStats.totalItems}
											/>
										);
									case 'eventManagement':
										return (
											<EventManagement
												rsvpData={rsvpData}
												rsvpUsers={rsvpUsers}
												sharedContacts={sharedContacts}
												reminderSettings={reminderSettings}
												setReminderSettings={updateReminderSettings}
												markReminderSettingsSaved={markReminderSettingsSaved}
												currentUser={effectiveUserData}
												loadDashboardData={loadDashboardData}
												formatDate={formatDate}
												cardStyle={cardStyle}
												isMobile={isMobile}
												width={width}
												isAdminOwnerPreview={isAdminOwnerPreview}
											/>
										);
									case 'registryStats':
										return (
											<RegistryStatistics
												dashboardStats={dashboardStats}
												formatCurrency={formatCurrency}
												cardStyle={[cardStyle, { flex: 1, minHeight: 380 }]}
												isMobile={isMobile}
												registryItems={registryItems}
												navigation={navigation}
												currentUserData={effectiveUserData}
											/>
										);
									case 'pollResults':
										if (polls.length === 0 && !isEditMode) return null;
										return (
											<PollResults
												polls={polls}
												formatDate={formatDate}
												cardStyle={[cardStyle, { flex: 1, minHeight: 380 }]}
												isMobile={isMobile}
											/>
										);
									case 'yearbookMessages':
										if (yearBookMessages.length === 0 && !isEditMode) return null;
										return (
											<YearbookMessages
												yearBookMessages={yearBookMessages}
												formatDate={formatDate}
												cardStyle={cardStyle}
												isMobile={isMobile}
												isPageOwner={true}
												currentUserUID={currentUser?.uid || currentUserData?.uid}
												onHideMessage={handleHideYearbookMessage}
												onDeleteMessage={handleDeleteYearbookMessage}
												onReplyMessage={handleReplyYearbookMessage}
											/>
										);
									default:
										return null;
								}
							};

							return (
								<View
									key={widgetItem.id}
									ref={widgetRefs[widgetItem.id]}
									style={{
										width: getWidgetWidth(widgetItem.size),
										opacity: widgetItem.visible ? 1 : 0.5,
										position: 'relative',
										transition: 'all 0.3s ease',
										alignSelf: 'stretch'
									}}
								>
									
									{/* Edit Mode Border & Controls */}
									{isEditMode && (
										<>
											{/* Beautiful semi-transparent border */}
											<View style={{
												position: 'absolute',
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												borderWidth: 3,
												borderColor: 'rgba(76, 149, 108, 0.35)',
												borderRadius: 24,
												pointerEvents: 'none',
												zIndex: 5
											}} />
											
											{/* Corner Accents */}
											<View style={{
												position: 'absolute',
												top: -1,
												left: -1,
												width: 24,
												height: 24,
												borderTopWidth: 4,
												borderLeftWidth: 4,
												borderColor: '#4c956c',
												borderTopLeftRadius: 14,
												zIndex: 10,
												pointerEvents: 'none'
											}} />
											<View style={{
												position: 'absolute',
												top: -1,
												right: -1,
												width: 24,
												height: 24,
												borderTopWidth: 4,
												borderRightWidth: 4,
												borderColor: '#4c956c',
												borderTopRightRadius: 14,
												zIndex: 10,
												pointerEvents: 'none'
											}} />
											<View style={{
												position: 'absolute',
												bottom: -1,
												left: -1,
												width: 24,
												height: 24,
												borderBottomWidth: 4,
												borderLeftWidth: 4,
												borderColor: '#4c956c',
												borderBottomLeftRadius: 14,
												zIndex: 10,
												pointerEvents: 'none'
											}} />
											
											{/* Resize Handle - Bottom Right Corner */}
											{!isMobile && (
												<TouchableOpacity
													onPress={() => cycleWidgetSize(widgetItem.id)}
													activeOpacity={0.7}
													style={{
														position: 'absolute',
														bottom: -1,
														right: -1,
														width: 32,
														height: 32,
														borderBottomRightRadius: 14,
														backgroundColor: '#4c956c',
														justifyContent: 'center',
														alignItems: 'center',
														zIndex: 30,
														shadowColor: '#000',
														shadowOffset: { width: 0, height: 2 },
														shadowOpacity: 0.2,
														shadowRadius: 4,
														elevation: 5
													}}
												>
													<Text style={{ 
														color: '#fff', 
														fontSize: 12, 
														fontWeight: '700'
													}}>
														{getSizeLabel(widgetItem.size)}
													</Text>
												</TouchableOpacity>
											)}
										</>
									)}
									
									{/* Widget Content */}
									<View style={{ pointerEvents: isEditMode ? 'none' : 'auto', flex: 1 }}>
										{renderWidgetContent()}
									</View>
								</View>
							);
						})}
					</View>}

				{/* Bottom padding */}
					<View style={{ height: 50 }} />
				</View>
			</ScrollView>
			
			{/* Share Modal */}
			<DashboardShareModal
				isVisible={showShareModal}
				onClose={() => setShowShareModal(false)}
				pageOwner={effectiveUserData}
				pageType="announcement"
				currentUser={currentUser}
				currentUserData={effectiveUserData}
			/>
			
		</ImageBackground>
	);
};

export default GraduateDashboard;